import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { ScheduleRoomRefresh } from '@theme/schedule-room';
import { findNextClass, localDateKey } from '@theme/ticket-schedule';

/**
 * Behavior for the Tickets & Classes dashboard (sections/registration-overview-global.liquid).
 *
 * Markup contract:
 * - `[data-schedule]` wraps one ticket's `[data-schedule-class]` items in schedule order,
 *   each with `data-date` (YYYY-MM-DD) and `data-time`. On event days, the next class to
 *   start gets `data-next-class`.
 * - `[data-kit]` wraps one kit action and carries `data-kit-state`
 *   (`available` | `adding` | `in-cart` | `error`). Its `[data-kit-add]` button carries
 *   `data-variant-id`, `data-ticket-id`, `data-class-id`, `data-class-name`, and
 *   `data-adding-label`; its `[data-kit-message]` carries `data-error-text`.
 * - `[data-qr-open]` buttons open `#ticket-qr-dialog` with `data-qr-url`, `data-qr-alt`,
 *   `data-qr-attendee`, `data-qr-fallback`, and `data-qr-title`.
 * - `a[data-jump]` links open the `<details>` they point to before scrolling.
 * - `[data-attendee-label]` elements with `data-ticket-id` and `data-fallback` mirror
 *   the attendee name after it is edited.
 * - With `data-schedule-rooms` on the element, `[data-schedule-room]` spans are filled from
 *   the registrations API on load, focus, attendee updates, and cross-tab schedule changes.
 *
 * @extends {Component}
 */
class TicketDashboard extends Component {
  /** @type {AbortController | undefined} */
  #listeners;
  #cartSequence = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const refresh = () => {
      this.markNextClasses();
      void this.syncKitsWithCart();
    };

    this.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('focus', refresh, { signal });
    document.addEventListener('visibilitychange', () => document.hidden || refresh(), { signal });
    document.addEventListener('attendee:updated', this.#handleAttendeeUpdated, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, () => void this.syncKitsWithCart(), { signal });

    if (this.hasAttribute('data-schedule-rooms')) this.#watchRooms(signal);
    refresh();

    // Shared or reloaded jump links can point at an attendee inside a collapsed section
    if (revealTarget(window.location.hash)) {
      document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    ++this.#cartSequence;
  }

  /**
   * Marks each ticket's next class with `data-next-class`.
   * @param {Date} [now] - Current time
   */
  markNextClasses(now = new Date()) {
    const today = localDateKey(now);

    for (const schedule of this.querySelectorAll('[data-schedule]')) {
      const items = /** @type {HTMLElement[]} */ ([...schedule.querySelectorAll('[data-schedule-class]')]);
      const classes = items.map(({ dataset }) => ({ date: dataset.date ?? '', time: dataset.time ?? '' }));
      const next = items[findNextClass(classes, now)];
      const show = next?.dataset.date === today;

      for (const item of items) item.toggleAttribute('data-next-class', show && item === next);
    }
  }

  /**
   * Adds one class's kit to the cart with the same line properties the registration page uses.
   * @param {HTMLButtonElement} button - The kit's add button
   */
  async addKit(button) {
    const kit = button.closest('[data-kit]');
    if (!(kit instanceof HTMLElement) || kit.dataset.kitState === 'adding') return;

    const { variantId, ticketId, classId, className } = button.dataset;
    ++this.#cartSequence;
    this.#setKitState(kit, 'adding');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { _ticket_id: ticketId, _class_id: classId, _kit: 'true', 'Class Name': className },
            },
          ],
        }),
      });
      if (!response.ok) {
        // Shopify explains refusals such as a sold-out kit; show that rather than a connection error
        const { description } = await response.json().catch(() => ({}));
        console.error('[TicketDashboard] Cart refused kit:', response.status, description);
        this.#setKitState(kit, 'error', description);
        return;
      }

      ++this.#cartSequence;
      this.#setKitState(kit, 'in-cart');
      await this.#announceCartUpdate();
    } catch (error) {
      console.error('[TicketDashboard] Could not add kit:', error);
      this.#setKitState(kit, 'error');
    }
  }

  /**
   * Reconciles kit actions with the latest cart, including removals in the drawer.
   */
  async syncKitsWithCart() {
    const buttons = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-kit-add]')]);
    if (buttons.length === 0) return;
    const sequence = ++this.#cartSequence;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
      if (!response.ok) return;
      const { items } = await response.json();
      if (sequence !== this.#cartSequence || !Array.isArray(items)) return;

      for (const button of buttons) {
        const { variantId, ticketId, classId } = button.dataset;
        const inCart = items.some((/** @type {any} */ item) => String(item.variant_id) === variantId && item.properties?._ticket_id === ticketId && classKey(item.properties?._class_id) === classKey(classId));
        const kit = button.closest('[data-kit]');
        if (!(kit instanceof HTMLElement) || kit.dataset.kitState === 'adding') continue;
        if (inCart) this.#setKitState(kit, 'in-cart');
        else if (kit.dataset.kitState === 'in-cart') this.#setKitState(kit, 'available');
      }
    } catch (error) {
      // The add buttons stay available; checkout still shows the real cart.
      console.error('[TicketDashboard] Could not read the cart:', error);
    }
  }

  /**
   * Keeps room labels current. Rooms come only from the registrations API, never the metafield.
   * @param {AbortSignal} signal - Aborted when the dashboard disconnects
   */
  #watchRooms(signal) {
    const subpath = /** @type {any} */ (window).REGISTRATION_DEV ? 'registration-dev' : 'registration';
    const rooms = new ScheduleRoomRefresh(this, `/apps/${subpath}/data/registrations`);
    const refresh = () => void rooms.refresh();

    window.addEventListener('focus', refresh, { signal });
    document.addEventListener('attendee:updated', refresh, { signal });
    window.addEventListener('storage', (event) => event.key === 'class_registration_schedule_changed' && refresh(), { signal });
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('class-registration-schedule');
      channel.addEventListener('message', refresh);
      signal.addEventListener('abort', () => channel.close());
    }
    refresh();
  }

  /**
   * @param {HTMLElement} kit - Kit action wrapper
   * @param {'available' | 'adding' | 'in-cart' | 'error'} state - New state
   * @param {string} [reason] - Why the cart refused the kit, shown instead of the connection message
   */
  #setKitState(kit, state, reason) {
    kit.dataset.kitState = state;

    const button = kit.querySelector('[data-kit-add]');
    if (button instanceof HTMLButtonElement) {
      button.dataset.label ??= button.textContent?.trim() ?? '';
      button.disabled = state === 'adding';
      button.textContent = state === 'adding' ? (button.dataset.addingLabel ?? '') : button.dataset.label;
    }

    // Writing the text (not just unhiding it) makes the alert region announce the error
    const message = kit.querySelector('[data-kit-message]');
    if (message instanceof HTMLElement) {
      message.textContent = state === 'error' ? reason || message.dataset.errorText || '' : '';
    }
  }

  async #announceCartUpdate() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;
      const cart = await response.json();
      this.dispatchEvent(new CartUpdateEvent(cart, this.id, { itemCount: cart.item_count, source: 'ticket-dashboard' }));
    } catch (error) {
      console.error('[TicketDashboard] Could not refresh the cart count:', error);
    }
  }

  /** @param {HTMLElement} button - The clicked code button */
  #openCode(button) {
    const dialog = /** @type {any} */ (document.getElementById('ticket-qr-dialog'));
    if (!dialog) return;

    const { qrUrl, qrAlt, qrAttendee, qrFallback, qrTitle } = button.dataset;
    const frame = dialog.querySelector('[data-qr-dialog-frame]');
    const attendee = dialog.querySelector('[data-qr-dialog-attendee]');
    const title = dialog.querySelector('[data-qr-dialog-title]');

    if (frame) {
      const image = frame.querySelector('img') ?? frame.appendChild(document.createElement('img'));
      image.className = 'ticket-qr__image';
      image.width = 320;
      image.height = 320;
      image.src = qrUrl ?? '';
      image.alt = qrAlt ?? '';
    }
    if (attendee) attendee.textContent = qrAttendee || qrFallback || '';
    if (title) title.textContent = qrTitle ?? '';

    dialog.showDialog();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;

    const kitButton = target?.closest('[data-kit-add]');
    if (kitButton instanceof HTMLButtonElement) {
      void this.addKit(kitButton);
      return;
    }

    const codeButton = target?.closest('[data-qr-open]');
    if (codeButton instanceof HTMLElement) {
      this.#openCode(codeButton);
      return;
    }

    const jump = target?.closest('a[data-jump]');
    if (jump instanceof HTMLAnchorElement) revealTarget(jump.hash);
  };

  /** @param {Event} event - `attendee:updated` from the name editor */
  #handleAttendeeUpdated = (event) => {
    const { ticketId, attendeeName } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (!ticketId) return;
    const selector = `[data-ticket-id="${CSS.escape(ticketId)}"]`;

    for (const label of this.querySelectorAll(`[data-attendee-label]${selector}`)) {
      if (label instanceof HTMLElement) label.textContent = attendeeName || label.dataset.fallback || '';
    }
    for (const button of this.querySelectorAll(`[data-qr-open]${selector}`)) {
      if (button instanceof HTMLElement) button.dataset.qrAttendee = attendeeName || '';
    }
  };
}

/**
 * Opens every collapsed section containing the element a URL hash points to.
 * @param {string} hash - `#id`, possibly percent-encoded
 * @returns {boolean} Whether anything was opened
 */
function revealTarget(hash) {
  if (!hash || hash.length < 2) return false;
  const target = document.getElementById(decodeURIComponent(hash.slice(1)));
  let details = target?.closest('details');
  let opened = false;

  while (details) {
    if (!details.open) {
      details.open = true;
      opened = true;
    }
    details = details.parentElement?.closest('details');
  }
  return opened;
}

/**
 * Class IDs arrive as numbers or Shopify GIDs depending on the source; compare the numeric tail.
 * @param {unknown} id
 * @returns {string}
 */
function classKey(id) {
  return (
    String(id ?? '')
      .split('/')
      .pop() ?? ''
  );
}

if (!customElements.get('ticket-dashboard')) customElements.define('ticket-dashboard', TicketDashboard);
